type DeprecatedConditionTypeOption = {
  isBeingDeprecated?: boolean;
};

type ConditionTypeLabelConfig = Record<string, unknown>;

export function formatConditionLabel(value: string) {
  return value
    .split('-')
    .map((part) => {
      if (part.toUpperCase() === 'V2') {
        return 'V2';
      }
      return part.charAt(0) + part.slice(1).toLowerCase();
    })
    .join(' ');
}

export function formatApplyTypeLabel(value: string) {
  return value
    .split('-')
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');
}

export type ConditionTypeCategory = 'user' | 'item' | 'flight';

const CONDITION_CATEGORY_KEYWORDS: Array<{
  category: ConditionTypeCategory;
  test: (value: string) => boolean;
}> = [
  {
    category: 'flight',
    test: (value) => value.includes('AIRPORT') || value.includes('FLIGHT'),
  },
  {
    category: 'user',
    test: (value) => value.includes('SUBSCRIBER') || value.includes('LOYALTY'),
  },
  {
    category: 'item',
    test: () => true,
  },
];

export function getConditionTypeCategory(
  conditionType: string,
  config?: ConditionTypeLabelConfig,
): ConditionTypeCategory {
  const categoryValue = (config?.category as string | undefined) ?? (config?.categoryBK as string | undefined);
  const configCategory = typeof categoryValue === 'string' ? categoryValue.toLowerCase() : '';
  if (configCategory === 'user' || configCategory === 'item' || configCategory === 'flight') {
    return configCategory;
  }

  const normalized = conditionType.toUpperCase();
  return CONDITION_CATEGORY_KEYWORDS.find(({ test }) => test(normalized))?.category ?? 'item';
}

export function formatConditionTypeOptionLabel(conditionType: string, config?: ConditionTypeLabelConfig) {
  return `${getConditionTypeCategory(conditionType, config)}: ${formatConditionLabel(conditionType)}`;
}

export const sortDeprecatedConditionTypes = <T extends DeprecatedConditionTypeOption>(a: T, b: T) => {
  if (a.isBeingDeprecated && !b.isBeingDeprecated) {
    return 1;
  }
  if (!a.isBeingDeprecated && b.isBeingDeprecated) {
    return -1;
  }
  return 0;
};

type ConditionTypeOptionLike = DeprecatedConditionTypeOption & {
  category?: string;
  label?: string;
  value?: string;
};

export const sortConditionTypeOptions = <T extends ConditionTypeOptionLike>(a: T, b: T) => {
  const categoryA = (a.category ?? '').toLowerCase();
  const categoryB = (b.category ?? '').toLowerCase();
  if (categoryA !== categoryB) {
    return categoryA.localeCompare(categoryB);
  }

  const deprecatedSort = sortDeprecatedConditionTypes(a, b);
  if (deprecatedSort !== 0) {
    return deprecatedSort;
  }

  const labelA = (a.label ?? a.value ?? '').toString();
  const labelB = (b.label ?? b.value ?? '').toString();
  return labelA.localeCompare(labelB);
};
